import type { UntypedFormArray } from '@angular/forms';
import { AbstractControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { isNotNullOrEmptyString } from '../../../../shared/utils/functions';
import { ValueUOMFG } from './floors.models';
import { FloorClass } from '../../../../models/floor.class';
import { DataObject } from '../../../../models/data-object.models';
import { amountAs, makeAmountWith } from '@miq/utils';
import { Units } from 'uom-units';

export class FloorsValidators {
  static floorNumberEmptyValidator(floorFG: UntypedFormGroup): { [key: string]: any } {
    const { floorId, floorNumber, floorName, hideFloor, floorArea, floorAreaUOM } = floorFG.value;

    return !isNotNullOrEmptyString(floorNumber) &&
      (isNotNullOrEmptyString(floorId) ||
        isNotNullOrEmptyString(floorName) ||
        hideFloor ||
        isNotNullOrEmptyString(floorArea) ||
        isNotNullOrEmptyString(floorAreaUOM))
      ? { emptyFloorNumber: { value: floorNumber } }
      : null;
  }

  static duplicatedFloorNumberValidator(floorsFA: UntypedFormArray): { [key: string]: any } {
    const duplicationCountObj: { [key: string]: number } = floorsFA.value.reduce((acc, floor) => {
      const currentFloorNumber = acc[floor.floorNumber];
      return isNotNullOrEmptyString(floor.floorNumber) && floor.floorIndex !== FloorClass.InvalidIndex
        ? { ...acc, [floor.floorNumber]: currentFloorNumber != null ? currentFloorNumber + 1 : 1 }
        : acc;
    }, {});
    const duplicatedValues: string[] = Object.entries(duplicationCountObj)
      .filter(([, count]) => count > 1)
      .map(([floorNumber]) => floorNumber);
    let result = null;
    floorsFA.controls.forEach(floorFG => {
      const currentErrors = { ...floorFG.errors };
      delete currentErrors.duplicatedFloorNumber;
      floorFG.setErrors(!!Object.keys(currentErrors).length ? { ...currentErrors } : null);
    });
    if (!!duplicatedValues.length) {
      floorsFA.controls
        .filter(floorFG =>
          duplicatedValues.includes(floorFG.value.floorNumber != null ? floorFG.value.floorNumber.toString() : null)
        )
        .forEach(floorFG =>
          floorFG.setErrors({ ...floorFG.errors, duplicatedFloorNumber: { value: floorFG.value.floorNumber } })
        );
      result = { duplicatedFloorNumber: { value: duplicatedValues } };
    }
    return result;
  }

  static notEmptyUOM(valueUomFG: UntypedFormGroup): { [key: string]: any } {
    const valueUOM: ValueUOMFG = valueUomFG.value;
    return isNotNullOrEmptyString(valueUOM.val) && !isNotNullOrEmptyString(valueUOM.uom) ? { emptyUOM: true } : null;
  }

  static numericValuesOnly(valueNumFG: UntypedFormGroup): { [key: string]: any } {
    const { val } = valueNumFG.value;
    return isNaN(Number(val)) ? { notANumber: true } : null;
  }

  static convertToSquareFeet(value: number, uom: string): number {
    if (isNaN(value) || uom == null || uom === '' || uom === Units.SquareFeet.name) {
      return value;
    }
    // Requested by PM to round up only when performing the validations
    // If there are decimals (not likely) those might be shown on the message errors
    // but the validation will be based on the rounded value
    const valueRounded = Math.round(value);
    return amountAs(Units.SquareFeet, makeAmountWith({ value: valueRounded, uom }));
  }

  static asWarning(...validators: ValidatorFn[]): ValidatorFn {
    return (control: AbstractControl): null => {
      const combinedErrors = validators.reduce(
        (acc, v) => {
          const result = v(control);
          return result ? { ...acc, ...result } : acc;
        },
        {} as Record<string, any>
      );
      (control as any).warnings = Object.keys(combinedErrors).length ? combinedErrors : null;
      return null;
    };
  }

  static minMaxValidation(
    minFloorArea: number,
    minFloorAreaUOM: string | undefined,
    maxFloorArea: number,
    maxFloorAreaUOM: string | undefined,
    commonFloorSize: number
  ): ValidatorFn {
    return (valueNumFG: UntypedFormGroup | any): DataObject | null => {
      const isNumber = (val: any): boolean => !isNaN(val);

      const ctrlUOM: string = valueNumFG.value?.uom;
      // * Force type conversion to ensure value is a number...
      let ctrlVal = parseFloat(valueNumFG.value?.val);
      minFloorArea = parseFloat(`${minFloorArea}`);
      maxFloorArea = parseFloat(`${maxFloorArea}`);
      commonFloorSize = parseFloat(`${commonFloorSize}`);

      const commonFSSqFeet = FloorsValidators.convertToSquareFeet(commonFloorSize, Units.SquareFeet.name);
      const minFASqFeet = FloorsValidators.convertToSquareFeet(minFloorArea, minFloorAreaUOM ?? Units.SquareFeet.name);
      const maxFASqFeet = FloorsValidators.convertToSquareFeet(maxFloorArea, maxFloorAreaUOM ?? Units.SquareFeet.name);
      const ctrlValSqFeet = FloorsValidators.convertToSquareFeet(ctrlVal, ctrlUOM);

      // * Only warning not an error...
      if (isNumber(maxFASqFeet) && minFASqFeet > maxFASqFeet) {
        // The Minimum floor plate is more than Maximum floor plate
        // xTranslate: minFloorMax
        valueNumFG['warnings'] = { ...(valueNumFG['warnings'] || {}), minGreaterThatMaxWarning: true };
      } else {
        const w = { ...(valueNumFG['warnings'] || {}) };
        delete w.minGreaterThatMaxWarning;
        valueNumFG['warnings'] = Object.keys(w).length ? w : null;
      }

      if (
        (!isNotNullOrEmptyString(commonFSSqFeet) && isNaN(ctrlValSqFeet)) ||
        (isNaN(minFASqFeet) && isNaN(maxFASqFeet))
      ) {
        return null;
      }

      if (minFASqFeet > ctrlValSqFeet && isNaN(maxFASqFeet)) {
        // "The Common Floor Size should not be less than {{valueA}}"
        // xTranslate: commonFloorShouldnotBLessThan
        valueNumFG['warnings'] = { ...(valueNumFG['warnings'] || {}), minFloorValidation: true };
        return null;
      }

      const wAfterMinCheck = { ...(valueNumFG['warnings'] || {}) };
      delete wAfterMinCheck.minFloorValidation;
      valueNumFG['warnings'] = Object.keys(wAfterMinCheck).length ? wAfterMinCheck : null;

      if (minFASqFeet < maxFASqFeet && (minFASqFeet > ctrlValSqFeet || ctrlValSqFeet > maxFASqFeet)) {
        // "The Common Floor Size should be between {{valueA}} and {{valueB}}",
        // xTranslate: commonFloorBtwn
        // "The Common Floor Size should not be more than {{valueA}}",
        // xTranslate: commonFloorShouldnotBeMoreThan
        valueNumFG['warnings'] = { ...(valueNumFG['warnings'] || {}), minMaxValidation: true };
        return null;
      }

      const wAfterMinMax = { ...(valueNumFG['warnings'] || {}) };
      delete wAfterMinMax.minMaxValidation;
      valueNumFG['warnings'] = Object.keys(wAfterMinMax).length ? wAfterMinMax : null;

      if (minFASqFeet === maxFASqFeet && isNumber(ctrlValSqFeet) && ctrlValSqFeet !== minFASqFeet) {
        // "Common Floor Size should be {{valueA}}",
        // xTranslate: commonFloorShould
        valueNumFG['warnings'] = { ...(valueNumFG['warnings'] || {}), commonFloorAreaValidation: true };
        return null;
      }

      const wAfterCommon = { ...(valueNumFG['warnings'] || {}) };
      delete wAfterCommon.commonFloorAreaValidation;
      valueNumFG['warnings'] = Object.keys(wAfterCommon).length ? wAfterCommon : null;

      return null;
    };
  }

  static commonFloorSizeValidators(
    minFloorArea: number | undefined,
    minFloorAreaUOM: string | undefined,
    maxFloorArea: number | undefined,
    maxFloorAreaUOM: string | undefined,
    commonFloorSize: number
  ): ValidatorFn[] {
    return [
      FloorsValidators.notEmptyUOM,
      FloorsValidators.numericValuesOnly,
      FloorsValidators.minMaxValidation(
        minFloorArea as number,
        minFloorAreaUOM,
        maxFloorArea as number,
        maxFloorAreaUOM,
        commonFloorSize
      ),
    ] as ValidatorFn[];
  }
}
